<script>
import Modal from "@/components/g-modal.vue";
import { isSafeInteger } from "lodash";
import { mapGetters } from "vuex";

/** @typedef Header
 * @property {string} [text]
 * @property {string} [value]
 * @property {string} [align]
 * @property {boolean} [sortable]
 */

/** @typedef FormData
 * @property {string} [descricao]
 * @property {number} [tipo_id]
 */

/** @typedef SelectOptions
 * @property {string} [text]
 * @property {number} [value]
 */

/**
 * @this {Vue & { clientId: number }}
 */
export default {
  components: {
    Modal
  },
  props: {
    businessPlanId: {
      type: [String, Number],
      required: true
    },
    isDemandant: Boolean,
    hasAdminRole: Boolean,
    opened: Boolean,
    rubricas: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      /** @type {FormData} */
      data: {
        descricao: null,
        tipo_id: 1 // Retorno Financeiro
      },
      /** @type {boolean} */
      innerOpened: this.opened,
      /** @type {import('../../businessplan.types').Rubrica[]} */
      rows: [],
      /** @type {string} */
      search: "",
      /** @type {import('../../businessplan.types').Rubrica[]} */
      selection: []
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    attatched() {
      return this.rubricas.map(({ id }) => id).filter(id => isSafeInteger(id));
    },
    attatchResource() {
      return this.apiResource(
        `v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/vpl/rubrica/vincular-rubricas`
      );
    },
    disabledAttatchBtn() {
      return !this.selection.length;
    },
    disabledCreateBtn() {
      const { descricao, tipo_id } = this.data;
      return !descricao || descricao.length < 3 || !tipo_id;
    },
    filteredRows() {
      return this.rows.filter(({ descricao }) => {
        const searchTerm = this.search.toLowerCase();
        return descricao.toLowerCase().includes(searchTerm);
      });
    },
    /**
     * @type {Header[]}
     * @readonly
     */
    headers() {
      const cols = [
        { text: "", value: "selection", align: "center", sortable: false },
        { text: "Descrição", value: "descricao" }
      ];

      if (!this.isDemandant) {
        cols.splice(1, 0, { text: "Tipo", value: "tipo", width: "160px" });
      }

      return cols;
    },
    rubricaResource() {
      return this.apiResource(
        `v1/ativacaoped/${this.clientId}/vpl/tipo-rubrica`
      );
    },
  },
  watch: {
    innerOpened(value) {
      this.$emit("update:opened", value);
    },
    opened(value) {
      if (value !== this.innerOpened) {
        this.innerOpened = value;
        this.selection = [];

        if (value) {
          this.clearForm();
          this.getRubricas();
        }
      }
    }
  },
  created() {
    if (this.opened) {
      this.getRubricas();
    }
  },
  methods: {
    clearForm() {
      this.data = {
        descricao: null,
        tipo_id: 1 // Retorno Financeiro
      };
    },
    close() {
      this.clearForm();
      this.innerOpened = false;
      this.$emit("update:opened", false);
    },
    async doAttatch() {
      try {
        const rubricas = this.selection.map(({ id }) => id);
        rubricas.push(...this.attatched);

        await this.attatchResource.save({ rubricas });

        const title =
          this.selection.length > 1
            ? "Retornos financeiros vinculados com sucesso"
            : "Retorno financeiro vinculado com sucesso";
        const text =
          this.selection.length > 1
            ? `${this.selection.length} retornos financeiros foram vinculados ao Business Plan.`
            : `Um retorno financeiro foi vinculado ao Business Plan.`;
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title,
          text
        });
        this.$emit("reload");
        this.innerOpened = false;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async doCreate() {
      try {
        const response = await this.rubricaResource.save(this.data);
        const tipo = this.data.tipo_id === 1 ? "Retorno Financeiro" : "Custo";

        try {
          this.selection.push(response);
          const rubricas = this.selection.map(({ id }) => id);
          rubricas.push(...this.attatched);

          await this.attatchResource.save({ rubricas });

          this.$emit("reload");
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "sucess",
            title: `${tipo} criado e vinculado com sucesso`,
            text: `${tipo} "${this.data.descricao}" está disponível para alocação de valores neste Business Plans.`
          });
        } catch (error) {
          if (!error.isAxiosError) {
            this.notify(error);
          }
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "alert",
            title: `${tipo} criado e não vinculado`,
            text: `${tipo} "${this.data.descricao}" foi criado, mas não está vinculado a este Business Plans.`
          });
        }

        this.getRubricas();
        this.close();
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async getRubricas() {
      try {
        const response = await this.rubricaResource.get();
        /** @type TipoRubrica[] */
        this.rows = (response?.data ?? [])
          .filter(({ descricao, id, tipo_id }) => {
            const isntAttatched = !this.attatched.includes(id);
            const naoEhOutrasDespesas = descricao
              ?.toLowerCase()
              .localeCompare("outras despesas");
            const userCanSee = !this.isDemandant || tipo_id === 1;
            return isntAttatched && naoEhOutrasDespesas && userCanSee;
          })
          .map(row => ({
            ...row,
            tipo: row.tipo_id === 1 ? "Retorno Financeiro" : "Custo"
          }))
          .sort(
            (
              { descricao: descricaoA, tipo: tipoA },
              { descricao: descricaoB, tipo: tipoB }
            ) =>
              tipoA.localeCompare(tipoB) || descricaoA.localeCompare(descricaoB)
          );
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.rows = [];
      }
    }
  }
};
</script>

<template>
  <Modal
    v-model="innerOpened"
    appendTitleIcon="mdi-link-plus"
    contentClass="vpl-attatch-modal"
    title="Adicionar Retornos Financeiros e Custos"
    width="800px"
  >
    <v-data-table
      v-model="selection"
      class="vpl-attach-data-table"
      disable-pagination
      fixed-header
      :headers="headers"
      hide-default-footer
      :items="filteredRows"
      :search="search"
      show-select
    >
      <template v-slot:top>
        <div class="sticky-top">
          <v-text-field
            v-model="search"
            class="search-field"
            clearable
            dense
            outlined
            placeholder="Filtrar..."
            prepend-inner-icon="mdi-filter-outline"
            hide-details
          ></v-text-field>
        </div>
      </template>

      <template v-slot:footer>
        <div class="sticky-footer vpl-attatch-form">
          <v-text-field
            v-model="data.descricao"
            dense
            hide-details
            outlined
            placeholder="Descrição do novo Retorno Financeiro"
          ></v-text-field>

          <v-btn
            color="primary"
            depressed
            :disabled="disabledCreateBtn"
            @click="doCreate"
          >
            Criar e vincular
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <template #buttons>
      <v-spacer />

      <v-btn class="px-5 ml-3" color="secondary" depressed @click="close">
        Cancelar
      </v-btn>

      <v-btn
        class="px-5 ml-3"
        color="primary"
        :dark="!disabledAttatchBtn"
        depressed
        :disabled="disabledAttatchBtn"
        @click="doAttatch"
      >
        Vincular
      </v-btn>
    </template>
  </Modal>
</template>

<style lang="scss">
.vpl-attatch-modal {
  & > .v-card > .v-card__text {
    padding: 0;
    overflow: hidden;
  }
}
</style>

<style lang="scss" scoped>
.search-field {
  margin: 0;
}

.vpl-attatch-form {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: auto 8rem;

  .v-btn {
    height: 40px;
  }
}

.vpl-attach-data-table {
  display: flex;
  flex-direction: column;
  max-height: 60vh;

  &::v-deep .v-data-table__wrapper {
    flex: 1;

    .tbody {
      flex: 1;
      overflow-y: auto;
    }
  }
}

.sticky-top {
  flex: 0;
  background: white;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
}

.sticky-footer {
  flex: 0;
  background: white;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
}
</style>
